import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { getRecentFiles } from "@/services/api/gallery";
import type {
  RecentFilesResponse,
  RecentFilesBucket,
} from "@/services/api/gallery";

const recentFilesKeys = {
  all: ["gallery", "recent"] as const,
  feed: () => [...recentFilesKeys.all, "feed"] as const,
};

const PAGE_SIZE = 20;

export function useRecentFiles() {
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery<RecentFilesResponse, Error>({
      queryKey: recentFilesKeys.feed(),
      queryFn: ({ pageParam }) => getRecentFiles(pageParam as number, PAGE_SIZE),
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        lastPage.hasMore ? lastPage.page + 1 : undefined,
      staleTime: 1 * 60 * 1000, 
    });

  const { buckets, totalCount } = useMemo(() => {
    if (!data) return { buckets: [] as RecentFilesBucket[], totalCount: 0 };

    const bucketMap = new Map<string, RecentFilesBucket["items"]>();
    const bucketOrder = ["Today", "Yesterday", "This week", "Earlier"];

    for (const page of data.pages) {
      for (const bucket of page.buckets) {
        const existing = bucketMap.get(bucket.label);
        if (existing) {
          existing.push(...bucket.items);
        } else {
          bucketMap.set(bucket.label, [...bucket.items]);
        }
      }
    }

    const merged: RecentFilesBucket[] = bucketOrder
      .filter((label) => bucketMap.has(label))
      .map((label) => ({ label, items: bucketMap.get(label)! }));

    return {
      buckets: merged,
      totalCount: data.pages[0]?.totalCount ?? 0,
    };
  }, [data]);

  return {
    buckets,
    isLoading,
    isError,
    totalCount,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
  };
}
